import { slug } from './content/helpers';
import { z } from 'zod';

export const callbackBusinessDayNames = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export const usTimeZones = [
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Marengo',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Chicago',
  'America/Indiana/Knox',
  'America/Indiana/Tell_City',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
] as const;

export const claimCardSchema = z.object({
  line1: z.string(),
  line2: z.string(),
  line3: z.string(),
});

export const claimLocaleSchema = z.object({
  name: z.string().max(100),
  path: slug,
  card: claimCardSchema,
  description: z.string(),
});

export const claimContentSchema = z.object({
  card: claimCardSchema,
  background_image: z.string(),
  description: z.string(),
  icon: z.string(),
});

export const claimMarketingAttrsSchema = z.object({
  offer: z.object({ id: z.number().int().positive() }),
  campaign: z.object({ id: z.number().int().positive() }),
  lead_type: z.object({
    id: z.number().int().positive(),
    name: z.string().min(1, 'Lead type name is required'),
  }),
  lead_source: z.object({
    id: z.number().int().positive(),
    name: z.string().min(1, 'Lead source name is required'),
    type: z.object({
      id: z.number().int().nonnegative(),
      name: z.string().min(1, 'Lead source type name is required'),
    }),
  }),
  lead_score_baseline: z.number().int().nonnegative(),
});

const callbackTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

const toMinuteOfDay = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const callbackBusinessIntervalSchema = z
  .object({
    start: callbackTimeSchema,
    end: callbackTimeSchema,
  })
  .refine(({ start, end }) => toMinuteOfDay(start) < toMinuteOfDay(end), {
    message: 'End time must be after start time',
    path: ['end'],
  });

const callbackBusinessDaySchema = z
  .array(callbackBusinessIntervalSchema)
  .superRefine((intervals, ctx) => {
    const sortedIntervals = [...intervals].sort(
      (current, next) => toMinuteOfDay(current.start) - toMinuteOfDay(next.start)
    );

    for (let index = 1; index < sortedIntervals.length; index += 1) {
      const previous = sortedIntervals[index - 1];
      const current = sortedIntervals[index];

      if (toMinuteOfDay(current.start) < toMinuteOfDay(previous.end)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Business hour intervals cannot overlap',
          path: [index, 'start'],
        });
      }
    }
  });

export const callbackSettingsSchema = z.object({
  tz: z.enum(usTimeZones),
  business_days: z.object({
    sunday: callbackBusinessDaySchema,
    monday: callbackBusinessDaySchema,
    tuesday: callbackBusinessDaySchema,
    wednesday: callbackBusinessDaySchema,
    thursday: callbackBusinessDaySchema,
    friday: callbackBusinessDaySchema,
    saturday: callbackBusinessDaySchema,
  }),
  slot_duration: z.number().int().positive().multipleOf(15),
});

export const claimSectionSchema = z.object({
  id: z.number().int().positive().optional(),
  name: z.string().min(1, 'Name is required').max(100),
  claim_type_id: z.number().int().positive(),
  path: z.string().max(100).optional(),
  visible: z.boolean(),
  i18n: z.object({
    es: claimLocaleSchema,
  }),
  content: claimContentSchema,
  marketing_attrs: claimMarketingAttrsSchema,
  callback_settings: callbackSettingsSchema.nullable().optional(),
});

export type ClaimSectionInput = z.infer<typeof claimSectionSchema>;
export type CallbackSettingsInput = z.infer<typeof callbackSettingsSchema>;
export type CallbackBusinessDayName = (typeof callbackBusinessDayNames)[number];
